/**
 * A200 — a row of filter chips as one stop in the Tab order (WAI-ARIA toolbar): Tab enters on the chip that is
 * on, ← → (and ↑ ↓, Début / Fin) move between chips, Entrée / Espace choose. Without it the ten shape chips
 * stood between the page header and the first frame.
 *
 * Returns `sync()`, to call after the chips change or another one is pressed.
 */
export function rovingToolbar(group: HTMLElement, selector: string): () => void {
  group.setAttribute('role', 'toolbar');
  const items = () => Array.from(group.querySelectorAll<HTMLElement>(selector)).filter(el => !el.hidden);
  const sync = () => {
    const list = items();
    const on = list.find(el => el.getAttribute('aria-pressed') === 'true') ?? list[0];
    for (const el of list) el.tabIndex = el === on ? 0 : -1;
  };
  group.addEventListener('keydown', event => {
    const list = items();
    const at = list.indexOf(document.activeElement as HTMLElement);
    if (at < 0) return;
    let next = -1;
    if (event.key === 'ArrowRight' || event.key === 'ArrowDown') next = (at + 1) % list.length;
    else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') next = (at - 1 + list.length) % list.length;
    else if (event.key === 'Home') next = 0;
    else if (event.key === 'End') next = list.length - 1;
    if (next < 0) return;
    event.preventDefault();
    for (const el of list) el.tabIndex = -1;
    const target = list[next]!;
    target.tabIndex = 0;
    target.focus();
    try { target.scrollIntoView({ block: 'nearest', inline: 'nearest' }); } catch { /* old browsers */ }
  });
  // Leaving the row: the stop goes back to the chip that is on.
  group.addEventListener('focusout', event => { if (!group.contains(event.relatedTarget as Node | null)) sync(); });
  sync();
  return sync;
}
